import React from "react";
import { Box, Container, Toolbar, useTheme, useMediaQuery } from "@mui/material";
import { Sidebar, ScreenInfo } from "./Sidebar";
import { TopBar } from "./TopBar";

interface MainLayoutProps {
  screens: ScreenInfo[];
  activeScreenId: string;
  onNavigate: (screenId: string) => void;
  children: React.ReactNode;
}

// LocalStorage key for sidebar state
const SIDEBAR_STATE_KEY = "sidebarOpen";

export const MainLayout: React.FC<MainLayoutProps> = ({
  screens,
  activeScreenId,
  onNavigate,
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Initialize sidebar state from localStorage (default: open on desktop, closed on mobile)
  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    if (typeof window === "undefined") return true;
    const saved = localStorage.getItem(SIDEBAR_STATE_KEY);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return !isMobile; // Default: open on desktop, closed on mobile
  });

  // Persist sidebar state to localStorage
  const handleToggleSidebar = React.useCallback(() => {
    setSidebarOpen((prev: boolean) => {
      const newState = !prev;
      localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(newState));
      return newState;
    });
  }, []);

  // Get current screen title for TopBar (including nested children)
  const findScreen = (items: ScreenInfo[], id: string): ScreenInfo | undefined => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children) {
        const found = findScreen(item.children, id);
        if (found) return found;
      }
    }
    return undefined;
  };
  const currentScreen = findScreen(screens, activeScreenId);
  const pageTitle = currentScreen?.name || "Admin";

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onToggle={handleToggleSidebar}
        screens={screens}
        activeScreenId={activeScreenId}
        onNavigate={onNavigate}
      />

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          width: "100%",
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Top bar - only visible on mobile */}
        {isMobile && (
          <>
            <TopBar
              onMenuClick={handleToggleSidebar}
              title={pageTitle}
              sidebarOpen={sidebarOpen}
            />
            {/* Toolbar spacer to push content below fixed AppBar */}
            <Toolbar />
          </>
        )}

        {/* Page content */}
        <Container
          maxWidth="xl"
          sx={{
            flex: 1,
            backgroundColor: "white",
            py: 2,
          }}
        >
          {children}
        </Container>
      </Box>
    </Box>
  );
};
